import { MONTHLY_MAPPED_COLUMN_PREFIX, PERIOD_MONTHLY, PERIOD_QUARTER, PERIOD_YEAR } from './constants'

/** DynamoDB/API の DataType 保存値（コード）を画面表示用ラベルに変換するマップ */
export const DATA_TYPE_DISPLAY_LABELS = {
  date: '日付型 (YYYY-MM-DD)',
  string: '文字列型',
  decimal: '小数型',
  int: '整数型'
}

/**
 * API の DataType コードを画面表示用ラベルに変換する。
 *
 * @param {string | undefined | null} columnDataType - データ型コード（date / string / decimal / int など）
 * @returns {string} 表示用ラベル。未定義・不正時は '-'
 */
export function getDataTypeDisplayLabel(columnDataType) {
  if (!columnDataType || typeof columnDataType !== 'string') return '-'
  const code = columnDataType.trim().toLowerCase()
  return DATA_TYPE_DISPLAY_LABELS[code] ?? columnDataType
}

/**
 * 大分類IDの表示用フォーマット。数値のみのID（例: "05", "04"）は先頭の0を除去する。
 *
 * @param {string} id - 大分類ID
 * @returns {string}
 */
export function formatMajorCategoryDisplayId(id) {
  if (id == null || String(id).trim() === '') return ''
  const s = String(id).trim()
  if (/^\d+$/.test(s)) return String(parseInt(s, 10))
  return s
}

/**
 * カテゴリ（大分類・中分類・ダッシュボード）の表示順比較。 英字始まり（S1, S2 など）を先に、数字始まり（1〜10）を後に、各グループ内は自然順（1,2,…,10）で並べる。
 * これによりマトリクスの行表示・プルダウンの並びを「S1, S2, 1, 2, …, 10」に統一する。
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function compareCategoryOrder(a, b) {
  const an = String(a ?? '')
  const bn = String(b ?? '')
  const aStartsDigit = /^\d/.test(an)
  const bStartsDigit = /^\d/.test(bn)
  if (aStartsDigit !== bStartsDigit) return aStartsDigit ? 1 : -1
  return an.localeCompare(bn, undefined, { numeric: true })
}

/**
 * 左カラム4階層を setting.json から組み立てる。
 *
 * @param {import('./types').SettingJson} settingJson
 * @returns {{
 *   majorName: string
 *   majorId: string
 *   majorLabel: string
 *   middleName: string
 *   dashboardName: string
 *   dashboardCode: string
 *   graphName: string
 *   graphId?: string
 *   required_columns: string[]
 *   optional_columns: string[]
 *   scope?: { standalone?: boolean; consolidate?: boolean }
 *   period_type?: string
 * }[]}
 */
export function buildLeftColumnFromSetting(settingJson) {
  const rows = []
  if (!settingJson || typeof settingJson !== 'object') return rows
  // 大分類を表示順（S1, S2, … → 1, 2, …）で並べる。id（S1 / 1 など）を基準にする。
  const topKeys = Object.keys(settingJson).sort((a, b) =>
    compareCategoryOrder(settingJson[a]?.id ?? a, settingJson[b]?.id ?? b)
  )
  for (const topKey of topKeys) {
    const block = settingJson[topKey]
    if (!block || !block.dashboardList) continue
    const majorName = block.name ?? topKey
    const majorId = block.id ?? topKey
    const displayId = formatMajorCategoryDisplayId(majorId)
    const majorLabel = displayId ? `${displayId} ${majorName}`.trim() : majorName
    // 中分類を表示順（S2.1, S2.2, … → 1.1, 1.2, …）で並べる。元配列は変更しない。
    const sortedMiddles = [...block.dashboardList].sort((a, b) => compareCategoryOrder(a.name ?? '', b.name ?? ''))
    for (const mid of sortedMiddles) {
      const middleName = mid.name ?? ''
      if (!mid.dashboard) continue
      // ダッシュボードを表示順（S1, S2, … → 1, 2, …, 10）で並べる。元配列は変更しない。
      const sortedDashboards = [...mid.dashboard].sort((x, y) => compareCategoryOrder(x.name ?? '', y.name ?? ''))
      for (const dash of sortedDashboards) {
        const dashboardName = dash.name ?? ''
        const dashboardCode = dash.id ?? dash.code ?? ''
        if (!dash.graph) continue
        for (const g of dash.graph) {
          rows.push({
            majorName,
            majorId,
            majorLabel,
            middleName,
            dashboardName,
            dashboardCode,
            graphName: g.name ?? '',
            // IDが存在しない場合は name で代替
            graphId: g.id ?? g.name ?? '',
            required_columns: Array.isArray(g.required_columns) ? g.required_columns : [],
            // 任意項目。旧 setting.json には存在しないため [] で後方互換を保つ
            optional_columns: Array.isArray(g.optional_columns) ? g.optional_columns : [],
            scope: g.scope,
            period_type: g.period_type
          })
        }
      }
    }
  }
  return rows
}

/**
 * モード・対象期間で左カラムをフィルタ。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} rows
 * @param {'standalone' | 'consolidate'} mode - 単体 = standalone, 連結＆セグメント = consolidate
 * @param {string} periodKind - 'month'|'quarter'|'year' (period_type の値)
 */
export function filterLeftColumnByModeAndPeriod(rows, mode, periodKind) {
  return rows.filter((r) => {
    const scope = r.scope
    const okScope = mode === 'standalone' ? scope?.standalone === true : scope?.consolidate === true
    const okPeriod = !periodKind || r.period_type === periodKind
    return okScope && okPeriod
  })
}

/** マトリクス横軸から除外するデータ分類コード（マスタ上 DataKindCode が none のグループ） */
const EXCLUDED_DATA_CLASSIFICATION_CODES_FOR_MATRIX = new Set(['none'])

/**
 * Get_all_chrofy_columns レスポンスを横軸用に整形（データ分類・項目列）。 DataKindCode が none の項目群はグループヘッダが「none」となりマトリクス用途に不向きなため除外する。
 *
 * @param {import('./types').ChrofyColumnsResponse} response
 * @returns {import('./types').ChrofyColumnsResponse}
 */
export function buildHorizontalFromChrofyColumns(response) {
  if (!Array.isArray(response)) return []
  return response.filter((block) => {
    const code = String(block?.data_classification_code ?? block?.DataClassificationCode ?? '')
      .trim()
      .toLowerCase()
    return !EXCLUDED_DATA_CLASSIFICATION_CODES_FOR_MATRIX.has(code)
  })
}

/**
 * API レスポンス（PascalCase）のデータ作成タスク一覧を camelCase 付きに変換する。
 *
 * @param {import('./types').DataCreateTaskResponse} taskList
 * @returns {{ periodKind?: string; periodValue?: string; key?: number; [key: string]: unknown }[]}
 */
export function mapDataCreateTaskListToCamelCase(taskList) {
  if (!Array.isArray(taskList)) return []
  return taskList.map((t) => ({
    ...t,
    periodKind: t.PeriodKind,
    periodValue: t.PeriodValue,
    key: t.Key
  }))
}

/**
 * 詳細期間一覧を PeriodKind でフィルタ。IF02028: monthly / quarter / year
 *
 * @param {import('./types').DataCreateTaskResponse} taskList
 * @param {string} periodKind - 'monthly'|'quarter'|'year'
 */
export function filterDetailPeriodByKind(taskList, periodKind) {
  if (!Array.isArray(taskList)) return []
  if (!periodKind) return taskList
  return taskList.filter((t) => (t.periodKind ?? t.PeriodKind) === periodKind)
}

/**
 * PeriodValue 文字列で新しい順（降順）に並べ替える比較関数。
 *
 * @param {{ periodValue?: string; PeriodValue?: string }} a
 * @param {{ periodValue?: string; PeriodValue?: string }} b
 * @returns {number}
 */
function compareDataCreateTaskPeriodValueDesc(a, b) {
  const va = String(a.periodValue ?? a.PeriodValue ?? '')
  const vb = String(b.periodValue ?? b.PeriodValue ?? '')
  return vb.localeCompare(va, undefined, { numeric: true })
}

/**
 * 詳細期間一覧を PeriodValue の新しい順（降順）で並べ替える。 人数カウント設定の「対象」年度リスト（fetchFiscalYearDateList: 新しい年度が先頭）と同趣旨。
 *
 * @param {import('./types').DataCreateTaskResponse} taskList
 * @returns {import('./types').DataCreateTaskResponse}
 */
export function sortDetailPeriodTasksByPeriodValueDesc(taskList) {
  if (!Array.isArray(taskList)) return []
  return [...taskList].sort(compareDataCreateTaskPeriodValueDesc)
}

/**
 * 詳細期間一覧を PeriodValue でソートし、最も新しい1件の Key を返す。設計書: PeriodValue で判定。
 *
 * @param {import('./types').DataCreateTaskResponse} options - FilterDetailPeriodByKind 済みの配列
 * @returns {number | null}
 */
export function getLatestDetailPeriodKey(options) {
  const sorted = sortDetailPeriodTasksByPeriodValueDesc(options)
  if (sorted.length === 0) return null
  const first = sorted[0]
  return first.key ?? first.Key ?? null
}

/**
 * 連続する同じキーを持つ行を結合するための rowSpan 配列を返す。 戻り値 span[i]: 行 i がグループの先頭なら結合する行数、そうでなければ 0（そのセルは描画しない）。
 *
 * @param {unknown[]} rows
 * @param {(r: unknown) => string} getKey
 * @returns {number[]}
 */
export function computeRowSpans(rows, getKey) {
  const n = rows.length
  const span = new Array(n).fill(0)
  for (let i = 0; i < n; i++) {
    const key = getKey(rows[i])
    const isFirst = i === 0 || getKey(rows[i - 1]) !== key
    if (!isFirst) continue
    let count = 1
    while (i + count < n && getKey(rows[i + count]) === key) count++
    span[i] = count
  }
  return span
}

/**
 * 行仮想スクロール用: 表示する行ウィンドウ [rowStart, rowEnd) について、結合セル（rowSpan）の値を算出する。 ウィンドウ先頭がグループの途中であっても、その行にラベルを描画し、span
 * はウィンドウ内に収まる行数に丸める（spacer 行へはみ出さない）。戻り値はウィンドウ内（rows[rowStart..rowEnd)）に対応する配列で、 値 0 の行はセルを描画しない（上の行の rowSpan に含まれる）。
 *
 * @param {unknown[]} rows - フィルタ済みの全行
 * @param {(r: unknown) => string} getKey - グループ判定キー
 * @param {number} rowStart - ウィンドウ先頭インデックス（含む）
 * @param {number} rowEnd - ウィンドウ末尾インデックス（含まない）
 * @returns {number[]} ウィンドウ内各行の rowSpan（0 は描画しない）
 */
export function computeWindowedRowSpans(rows, getKey, rowStart, rowEnd) {
  const start = Math.max(0, rowStart)
  const end = Math.min(rows.length, rowEnd)
  const eff = new Array(Math.max(0, end - start)).fill(0)
  let i = start
  while (i < end) {
    const key = getKey(rows[i])
    let j = i + 1
    // 走査をウィンドウ末尾 end で打ち切る（結合セルは spacer 行へはみ出さないため end 超の走査は不要）。
    // これにより 1 スクロールあたりの計算量を O(行数) ではなく O(ウィンドウ) に保つ。end <= rows.length なので添字は安全。
    while (j < end && getKey(rows[j]) === key) j++
    eff[i - start] = j - i
    i = j
  }
  return eff
}

/**
 * 行フィルタ（大分類・中分類・ダッシュボード・グラフ・参照）で左カラム行を絞り込み。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} rows
 * @param {{
 *   major?: string[]
 *   middle?: string[]
 *   dashboard?: string[]
 *   graph?: string[]
 *   referable?: 'all' | 'referable' | 'unreferable'
 * }} rowFilters
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {import('./types').GenericMappingListResponse} [mappingListMonthly] - 年次・四半期時の G0006 系用
 * @param {string} [periodKind] - Monthly / quarter / year（省略時は月次相当で主一覧のみ）
 */
export function filterLeftColumnByRowFilters(
  rows,
  rowFilters,
  mappingList,
  mappingListMonthly = [],
  periodKind = PERIOD_MONTHLY
) {
  if (!rowFilters || Object.keys(rowFilters).length === 0) return rows
  return rows.filter((r) => {
    if (Array.isArray(rowFilters.major)) {
      if (rowFilters.major.length === 0) return false
      if (!rowFilters.major.includes(r.majorLabel)) return false
    }
    if (Array.isArray(rowFilters.middle)) {
      if (rowFilters.middle.length === 0) return false
      if (!rowFilters.middle.includes(r.middleName)) return false
    }
    if (Array.isArray(rowFilters.dashboard)) {
      if (rowFilters.dashboard.length === 0) return false
      if (!rowFilters.dashboard.includes(r.dashboardName)) return false
    }
    if (Array.isArray(rowFilters.graph)) {
      if (rowFilters.graph.length === 0) return false
      if (!rowFilters.graph.includes(r.graphName)) return false
    }
    if (rowFilters.referable && rowFilters.referable !== 'all') {
      const referable = (r.required_columns || []).every((code) => {
        const status = computeMappingStatusDual(mappingList, mappingListMonthly, periodKind, [code])
        return status[code] === 'set'
      })
      if (rowFilters.referable === 'referable' && !referable) return false
      if (rowFilters.referable === 'unreferable' && referable) return false
    }
    return true
  })
}

/**
 * 必須項目の column_code 一覧に対して、マッピング一覧から「未設定/不要/設定済」を判定。
 *
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {string[]} requiredColumnCodes
 * @returns {{ [columnCode: string]: 'unset' | 'remove' | 'set' }}
 */
export function computeMappingStatus(mappingList, requiredColumnCodes) {
  const status = {}
  const mapByKey = new Map()
  for (const m of mappingList || []) {
    const key = `${m.data_classification_code}#${m.column_code}`
    if (!mapByKey.has(key)) mapByKey.set(key, m)
  }
  for (const code of requiredColumnCodes || []) {
    let found = false
    let mappingType = ''
    for (const [, m] of mapByKey) {
      if (m.column_code === code) {
        found = true
        mappingType = m.mapping_type ?? ''
        break
      }
    }
    if (!found) status[code] = 'unset'
    else if (mappingType === 'remove') status[code] = 'remove'
    else status[code] = 'set'
  }
  return status
}

/**
 * 年次・四半期かつ G0006 接頭辞の列は月次マッピング一覧を参照する（ヘッダドット・モーダル分割と同一条件）。
 *
 * @param {string} periodKind
 * @param {string} columnCode
 * @returns {boolean}
 */
export function usesMonthlyMappingListForMatrix(periodKind, columnCode) {
  return (
    (periodKind === PERIOD_YEAR || periodKind === PERIOD_QUARTER) &&
    Boolean(columnCode) &&
    typeof columnCode === 'string' &&
    columnCode.startsWith(MONTHLY_MAPPED_COLUMN_PREFIX)
  )
}

/**
 * 年次・四半期かつ G0006 接頭辞の列は mappingListMonthly、それ以外は mappingList を参照する
 *
 * @param {string} periodKind
 * @param {string} columnCode
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {import('./types').GenericMappingListResponse} mappingListMonthly
 * @returns {import('./types').GenericMappingListResponse}
 */
export function selectMappingListForColumn(periodKind, columnCode, mappingList, mappingListMonthly) {
  const monthly = mappingListMonthly ?? []
  return usesMonthlyMappingListForMatrix(periodKind, columnCode) ? monthly : mappingList || []
}

/**
 * 列ごとに主マッピング／月次マッピングを切り替えて未設定・不要・設定済を返す
 *
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {import('./types').GenericMappingListResponse} mappingListMonthly
 * @param {string} periodKind
 * @param {string[]} requiredColumnCodes
 * @returns {{ [columnCode: string]: 'unset' | 'remove' | 'set' }}
 */
export function computeMappingStatusDual(mappingList, mappingListMonthly, periodKind, requiredColumnCodes) {
  const status = {}
  for (const code of requiredColumnCodes || []) {
    const list = selectMappingListForColumn(periodKind, code, mappingList, mappingListMonthly)
    const one = computeMappingStatus(list, [code])
    status[code] = one[code] ?? 'unset'
  }
  return status
}

/**
 * 横軸をブロックからフラットな列リストに（データ分類名・column_code・column_name）
 *
 * @param {import('./types').ChrofyColumnsResponse} blocks
 * @returns {{
 *   column_code: string
 *   column_name: string
 *   data_classification_name: string
 *   data_classification_code: string
 * }[]}
 */
export function flattenHorizontalColumns(blocks) {
  if (!Array.isArray(blocks)) return []
  return blocks.flatMap((b) =>
    (b.columns || []).map((c) => ({
      ...c,
      data_classification_name: b.data_classification_name,
      data_classification_code: b.data_classification_code
    }))
  )
}

/**
 * 列を検索・設定済/未設定フィルタで絞り込み
 *
 * @param {ReturnType<typeof flattenHorizontalColumns>} flatColumns
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {{ searchText: string; checkedSet?: boolean; checkedUnset?: boolean }} filters
 * @param {import('./types').GenericMappingListResponse} [mappingListMonthly]
 * @param {string} [periodKind]
 * @returns {ReturnType<typeof flattenHorizontalColumns>}
 */
export function filterHorizontalColumns(
  flatColumns,
  mappingList,
  filters,
  mappingListMonthly = [],
  periodKind = PERIOD_MONTHLY
) {
  let list = flatColumns
  if (filters.searchText.trim()) {
    const q = filters.searchText.trim().toLowerCase()
    list = list.filter(
      (c) =>
        (c.column_name && c.column_name.toLowerCase().includes(q)) ||
        (c.column_code && c.column_code.toLowerCase().includes(q))
    )
  }
  if (filters.checkedSet || filters.checkedUnset) {
    list = list.filter((c) => {
      const src = selectMappingListForColumn(periodKind, c.column_code, mappingList, mappingListMonthly)
      let typ
      for (const m of src || []) {
        if (m.column_code === c.column_code) {
          typ = m.mapping_type
          break
        }
      }
      const isSet = typ && typ !== 'remove'
      const isUnset = !typ
      const isRemove = typ === 'remove'
      if (filters.checkedSet) return isSet
      if (filters.checkedUnset) return isUnset || isRemove
      return true
    })
  }
  return list
}

/**
 * 行の参照可否。グラフに紐づく必須 CHROFY 項目がすべて「設定済」なら参照可能とみなす。 年次・四半期では G0006 系項目は月次タスク由来のマッピングで上書き判定する（ヘッダのドットと同じ二系統ルール）。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>[0]} row
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {import('./types').GenericMappingListResponse} [mappingListMonthly]
 * @param {string} [periodKind]
 * @returns {boolean}
 */
export function isRowReferable(row, mappingList, mappingListMonthly = [], periodKind = PERIOD_MONTHLY) {
  const monthly = mappingListMonthly ?? []
  const status = computeMappingStatusDual(mappingList, monthly, periodKind, row.required_columns || [])
  return (row.required_columns || []).every((code) => status[code] === 'set')
}

/**
 * サマリ集計（表示中行・列に基づく）。参照可能ダッシュボード＝1つでも参照可能グラフを持つダッシュボード。 カード上の件数は画面上のドット・行の「参照」と同じく、主マッピング＋必要時の月次マッピングを併用して数える。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} leftRows
 * @param {ReturnType<typeof flattenHorizontalColumns>} flatFilteredColumns
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {import('./types').GenericMappingListResponse} [mappingListMonthly]
 * @param {string} [periodKind]
 * @returns {{
 *   setCount: number
 *   totalCount: number
 *   referableGraphCount: number
 *   totalGraphCount: number
 *   referableDashboardCount: number
 *   totalDashboardCount: number
 * }}
 */
export function computeSummary(
  leftRows,
  flatFilteredColumns,
  mappingList,
  mappingListMonthly = [],
  periodKind = PERIOD_MONTHLY
) {
  const monthly = mappingListMonthly ?? []
  // 総項目: ヘッダーに表示されている全CHROFY項目数
  const totalCount = flatFilteredColumns.length

  // 設定済項目: ヘッダーでステータスが'set'(緑)の項目数
  let setCount = 0
  for (const col of flatFilteredColumns) {
    const status = computeMappingStatusDual(mappingList, monthly, periodKind, [col.column_code])
    if (status[col.column_code] === 'set') {
      setCount += 1
    }
  }

  let referableGraphCount = 0
  const dashboardKeyToReferable = new Map()
  for (const row of leftRows) {
    const ref = isRowReferable(row, mappingList, monthly, periodKind)
    if (ref) referableGraphCount += 1
    const dk = `${row.majorId}|${row.middleName}|${row.dashboardName}`
    if (!dashboardKeyToReferable.has(dk)) dashboardKeyToReferable.set(dk, ref)
    else if (ref) dashboardKeyToReferable.set(dk, true)
  }
  const totalDashboardCount = dashboardKeyToReferable.size
  let referableDashboardCount = 0
  for (const ref of dashboardKeyToReferable.values()) {
    if (ref) referableDashboardCount += 1
  }
  return {
    setCount,
    totalCount,
    referableGraphCount,
    totalGraphCount: leftRows.length,
    referableDashboardCount,
    totalDashboardCount
  }
}

/**
 * 総数（モード・対象期間のみフィルタ、表示フィルタは反映しない）
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} leftColumnRows
 * @param {ReturnType<typeof flattenHorizontalColumns>} flatColumns
 * @returns {{ totalItems: number; totalDashboards: number; totalGraphs: number }}
 */
export function computeTotals(leftColumnRows, flatColumns) {
  const totalGraphs = leftColumnRows.length
  const totalItems = flatColumns.length
  const dashKeys = new Set(leftColumnRows.map((r) => `${r.majorId}|${r.middleName}|${r.dashboardName}`))
  const totalDashboards = dashKeys.size
  return { totalItems, totalDashboards, totalGraphs }
}

/**
 * モーダル用に 必須 + 任意 項目を set/unset/remove にグループ化（表示名）。 マトリクス上の色分けと齟齬が出ないよう、ヘッダと同じ二系統マッピングで状態を求める。
 * 年次・四半期時は月次タスク由来と主タスク由来でチップを分割するための配列も付与する。 任意 項目は `optionalNames` にも入れ、モーダル側で枠線のみのチップとして描く。
 *
 * @param {string[]} requiredColumnCodes
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {Map<string, string>} codeToName
 * @param {import('./types').GenericMappingListResponse} [mappingListMonthly]
 * @param {string} [periodKind]
 * @param {string[]} [optionalColumnCodes] - 任意項目コード。必須 と重複する分は除外される
 * @returns {{
 *   set: string[]
 *   unset: string[]
 *   remove: string[]
 *   setFromMonthlyTask?: string[]
 *   setFromPrimaryTask?: string[]
 *   unsetFromMonthlyTask?: string[]
 *   unsetFromPrimaryTask?: string[]
 *   removeFromMonthlyTask?: string[]
 *   removeFromPrimaryTask?: string[]
 *   optionalNames?: string[]
 * }}
 */
export function groupRequiredColumnsForModal(
  requiredColumnCodes,
  mappingList,
  codeToName,
  mappingListMonthly = [],
  periodKind = PERIOD_MONTHLY,
  optionalColumnCodes = []
) {
  const monthly = mappingListMonthly ?? []
  // 必須 と 任意 を 1 つの配列にまとめて状態を求める。必須 が優先（重複時）
  const requiredCodes = requiredColumnCodes || []
  const requiredSet = new Set(requiredCodes)
  const optionalCodes = (optionalColumnCodes || []).filter((c) => !requiredSet.has(c))
  const allCodes = [...requiredCodes, ...optionalCodes]
  const status = computeMappingStatusDual(mappingList, monthly, periodKind, allCodes)
  // 任意 のチップは枠線のみで描くため、名称の集合を返す
  const optionalNames = []
  const set = []
  const unset = []
  const remove = []
  const isDualPeriod = periodKind === PERIOD_YEAR || periodKind === PERIOD_QUARTER
  const setFromMonthlyTask = []
  const setFromPrimaryTask = []
  const unsetFromMonthlyTask = []
  const unsetFromPrimaryTask = []
  const removeFromMonthlyTask = []
  const removeFromPrimaryTask = []

  for (const code of allCodes) {
    const name = codeToName.get(code) || code
    const st = status[code]
    if (!requiredSet.has(code)) optionalNames.push(name)
    const monthlyBranch = usesMonthlyMappingListForMatrix(periodKind, code)
    if (st === 'set') {
      set.push(name)
      if (isDualPeriod) {
        if (monthlyBranch) setFromMonthlyTask.push(name)
        else setFromPrimaryTask.push(name)
      }
    } else if (st === 'unset') {
      unset.push(name)
      if (isDualPeriod) {
        if (monthlyBranch) unsetFromMonthlyTask.push(name)
        else unsetFromPrimaryTask.push(name)
      }
    } else {
      remove.push(name)
      if (isDualPeriod) {
        if (monthlyBranch) removeFromMonthlyTask.push(name)
        else removeFromPrimaryTask.push(name)
      }
    }
  }

  if (!isDualPeriod) {
    return { set, unset, remove, optionalNames }
  }
  return {
    set,
    unset,
    remove,
    optionalNames,
    setFromMonthlyTask,
    setFromPrimaryTask,
    unsetFromMonthlyTask,
    unsetFromPrimaryTask,
    removeFromMonthlyTask,
    removeFromPrimaryTask
  }
}

/**
 * 行フィルタ連動: rowFilters から選択肢（major/middle/dashboard/graph）を算出
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} leftColumnRows
 * @param {{ major?: string[]; middle?: string[]; dashboard?: string[]; graph?: string[] }} rf
 * @returns {{ major: string[]; middle: string[]; dashboard: string[]; graph: string[] }}
 */
export function computeRowFilterOptionsFromRf(leftColumnRows, rf) {
  // 並びは leftColumnRows（buildLeftColumnFromSetting で表示順にソート済み）の出現順をそのまま使い、 ソートし直さない。これでプルダウンの順序が表（行表示）と完全に一致する。
  const major = [...new Set(leftColumnRows.map((r) => r.majorLabel))].filter(Boolean)
  const rowsByMajor =
    rf.major?.length > 0 ? leftColumnRows.filter((r) => rf.major.includes(r.majorLabel)) : leftColumnRows
  const middle = [...new Set(rowsByMajor.map((r) => r.middleName))].filter(Boolean)
  const rowsByMiddle = rf.middle?.length > 0 ? rowsByMajor.filter((r) => rf.middle.includes(r.middleName)) : rowsByMajor
  const dashboard = [...new Set(rowsByMiddle.map((r) => r.dashboardName))].filter(Boolean)
  const rowsByDashboard =
    rf.dashboard?.length > 0 ? rowsByMiddle.filter((r) => rf.dashboard.includes(r.dashboardName)) : rowsByMiddle
  const graph = [...new Set(rowsByDashboard.map((r) => r.graphName))].filter(Boolean)
  return { major, middle, dashboard, graph }
}

/**
 * 行フィルタの「すべて」判定: 現在選択が全件、または拡張後に「すべて」のままなら「すべて」ラベルを返す
 *
 * @param {string[]} curr - 現在の選択値
 * @param {string[]} opt - 選択肢
 * @param {string[]} [prev] - 前回の選択肢
 * @returns {string} 'すべて' または 'N/M 件'
 */
export function getRowFilterCountLabel(curr, opt, prev) {
  const normalAll = curr.length === opt.length
  const expandedHadAll =
    prev?.length > 0 && opt.length > prev.length && curr.length === prev.length && prev.every((x) => curr.includes(x))
  return normalAll || expandedHadAll ? 'すべて' : `${curr.length}/${opt.length} 件`
}

/**
 * 行フィルタの連動（cascade）。上位階層の選択が変わると下位の候補集合が変わるため、 いま選んでいる値のうち「もう存在しないもの」を落とし、空になったら下位を「すべて選択」相当で埋め直す。
 *
 * @param {{ major?: string[]; middle?: string[]; dashboard?: string[]; graph?: string[] }} mergedRf - マージ後の rowFilters
 * @param {{ major: string[]; middle: string[]; dashboard: string[]; graph: string[] }} nextOpt - 新しい選択肢
 * @param {{ major: string[]; middle: string[]; dashboard: string[]; graph: string[] }} prevOpt - 前の選択肢
 * @returns {{ middle: string[]; dashboard: string[]; graph: string[] }}
 */
export function cascadeRowFilters(mergedRf, nextOpt, prevOpt) {
  const middleOk = mergedRf.middle.filter((m) => nextOpt.middle.includes(m))
  const middleWasAll = mergedRf.middle.length === prevOpt.middle.length && prevOpt.middle.length > 0
  const middleNext =
    middleOk.length > 0
      ? middleWasAll && nextOpt.middle.length > prevOpt.middle.length
        ? [...nextOpt.middle]
        : middleOk
      : mergedRf.middle.length > 0
        ? [...nextOpt.middle]
        : []

  const dashboardOk = mergedRf.dashboard.filter((d) => nextOpt.dashboard.includes(d))
  const dashboardWasAll = mergedRf.dashboard.length === prevOpt.dashboard.length && prevOpt.dashboard.length > 0
  const dashboardNext =
    dashboardOk.length > 0
      ? dashboardWasAll && nextOpt.dashboard.length > prevOpt.dashboard.length
        ? [...nextOpt.dashboard]
        : dashboardOk
      : mergedRf.dashboard.length > 0
        ? [...nextOpt.dashboard]
        : []

  const graphOk = mergedRf.graph.filter((g) => nextOpt.graph.includes(g))
  const graphWasAll = mergedRf.graph.length === prevOpt.graph.length && prevOpt.graph.length > 0
  const graphNext =
    graphOk.length > 0
      ? graphWasAll && nextOpt.graph.length > prevOpt.graph.length
        ? [...nextOpt.graph]
        : graphOk
      : mergedRf.graph.length > 0
        ? [...nextOpt.graph]
        : []

  return { middle: middleNext, dashboard: dashboardNext, graph: graphNext }
}

/**
 * データ分類で列をグループ化
 *
 * @param {ReturnType<typeof flattenHorizontalColumns>} columns
 * @returns {{
 *   data_classification_code: string
 *   data_classification_name: string
 *   columns: ReturnType<typeof flattenHorizontalColumns>
 * }[]}
 */
export function groupByDataClassification(columns) {
  const groups = []
  for (const col of columns) {
    const last = groups[groups.length - 1]
    if (last && last.data_classification_code === col.data_classification_code) {
      last.columns.push(col)
    } else {
      groups.push({
        data_classification_code: col.data_classification_code,
        data_classification_name: col.data_classification_name || col.data_classification_code,
        columns: [col]
      })
    }
  }
  return groups
}

/**
 * PeriodKind を periodType に変換（filter 用）
 *
 * @param {string} periodKind - 'monthly'|'quarter'|'year'
 * @returns {'month' | 'quarter' | 'year'}
 */
export function periodTypeForFilter(periodKind) {
  return periodKind === 'monthly' ? 'month' : periodKind === 'quarter' ? 'quarter' : 'year'
}

/**
 * 行フィルタを全選択状態にリセット（leftColumnRows から選択肢を構築）
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} leftColumnRows
 * @returns {{ major: string[]; middle: string[]; dashboard: string[]; graph: string[]; referable: 'all' }}
 */
export function buildAllRowFilters(leftColumnRows) {
  return {
    major: [...new Set(leftColumnRows.map((r) => r.majorLabel))].filter(Boolean).sort(compareCategoryOrder),
    middle: [...new Set(leftColumnRows.map((r) => r.middleName))].filter(Boolean).sort(compareCategoryOrder),
    dashboard: [...new Set(leftColumnRows.map((r) => r.dashboardName))].filter(Boolean).sort(compareCategoryOrder),
    graph: [...new Set(leftColumnRows.map((r) => r.graphName))].filter(Boolean).sort(),
    referable: 'all'
  }
}

// ---------------------------------------------------------------------------
// 事前計算（precompute）: マッピング状態を column_code 単位で 1 回だけ求めるための関数群。
// 既存の computeMappingStatusDual / computeMappingStatus / isRowReferable / computeSummary は
// 単体テストおよびモーダルがそのまま参照するため、シグネチャ・挙動を変更しない。
// 下記の *FromStatus 系は、事前計算済み statusByCode を受け取って O(1) 参照に置き換えるための新規 API。
// ---------------------------------------------------------------------------

/**
 * 1 系統のマッピング一覧から「column_code → mapping_type」の索引を作る。 computeMappingStatus と同じ重複規則（`data_classification_code#column_code`
 * 単位で最初の 1 件を採用し、同一 column_code が複数分類に跨る場合は出現順で最初の mapping_type を採用）を再現する。
 *
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @returns {Map<string, string>} Column_code → mapping_type（空文字を含む）
 */
function buildMappingTypeByCode(mappingList) {
  const seenKeys = new Set()
  const typeByCode = new Map()
  for (const m of mappingList || []) {
    const key = `${m.data_classification_code}#${m.column_code}`
    if (seenKeys.has(key)) continue
    seenKeys.add(key)
    if (!typeByCode.has(m.column_code)) typeByCode.set(m.column_code, m.mapping_type ?? '')
  }
  return typeByCode
}

/**
 * 与えられた column_code 群すべてについて、主／月次マッピングを使い分けて「未設定/不要/設定済」を 1 回でまとめて求める。 computeMappingStatusDual を code
 * ごとに呼ぶのと同じ結果を返すが、マッピング一覧の走査を 2 回（主・月次）に抑える。
 *
 * 注意: 呼び出し側は横軸の全 column_code に加え、各行の required_columns に含まれる code も columnCodes に含めること（行の参照可否判定で参照されるため）。
 *
 * @param {Iterable<string>} columnCodes - 状態を求めたい column_code の集合
 * @param {import('./types').GenericMappingListResponse} mappingList
 * @param {import('./types').GenericMappingListResponse} [mappingListMonthly]
 * @param {string} [periodKind]
 * @returns {{ [columnCode: string]: 'unset' | 'remove' | 'set' }}
 */
export function buildStatusByCode(columnCodes, mappingList, mappingListMonthly = [], periodKind = PERIOD_MONTHLY) {
  const primaryType = buildMappingTypeByCode(mappingList)
  const monthlyType = buildMappingTypeByCode(mappingListMonthly ?? [])
  const status = {}
  for (const code of columnCodes || []) {
    if (status[code] !== undefined) continue
    const typeMap = usesMonthlyMappingListForMatrix(periodKind, code) ? monthlyType : primaryType
    if (!typeMap.has(code)) {
      status[code] = 'unset'
    } else {
      status[code] = typeMap.get(code) === 'remove' ? 'remove' : 'set'
    }
  }
  return status
}

/**
 * 事前計算済み statusByCode を使った行の参照可否判定（isRowReferable と等価）。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>[0]} row
 * @param {{ [columnCode: string]: 'unset' | 'remove' | 'set' }} statusByCode
 * @returns {boolean}
 */
export function isRowReferableFromStatus(row, statusByCode) {
  return (row.required_columns || []).every((code) => statusByCode[code] === 'set')
}

/**
 * 事前計算済み statusByCode を使ったサマリ集計（computeSummary と等価）。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} leftRows
 * @param {ReturnType<typeof flattenHorizontalColumns>} flatFilteredColumns
 * @param {{ [columnCode: string]: 'unset' | 'remove' | 'set' }} statusByCode
 * @returns {{
 *   setCount: number
 *   totalCount: number
 *   referableGraphCount: number
 *   totalGraphCount: number
 *   referableDashboardCount: number
 *   totalDashboardCount: number
 * }}
 */
export function computeSummaryFromStatus(leftRows, flatFilteredColumns, statusByCode) {
  const totalCount = flatFilteredColumns.length
  let setCount = 0
  for (const col of flatFilteredColumns) {
    if (statusByCode[col.column_code] === 'set') setCount += 1
  }

  let referableGraphCount = 0
  const dashboardKeyToReferable = new Map()
  for (const row of leftRows) {
    const ref = isRowReferableFromStatus(row, statusByCode)
    if (ref) referableGraphCount += 1
    const dk = `${row.majorId}|${row.middleName}|${row.dashboardName}`
    if (!dashboardKeyToReferable.has(dk)) dashboardKeyToReferable.set(dk, ref)
    else if (ref) dashboardKeyToReferable.set(dk, true)
  }
  const totalDashboardCount = dashboardKeyToReferable.size
  let referableDashboardCount = 0
  for (const ref of dashboardKeyToReferable.values()) {
    if (ref) referableDashboardCount += 1
  }
  return {
    setCount,
    totalCount,
    referableGraphCount,
    totalGraphCount: leftRows.length,
    referableDashboardCount,
    totalDashboardCount
  }
}

/**
 * 事前計算済み statusByCode を使った行フィルタ（filterLeftColumnByRowFilters と等価）。 referable 判定のみ statusByCode
 * を参照し、それ以外（major/middle/dashboard/graph）は同一ロジック。
 *
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} rows
 * @param {{
 *   major?: string[]
 *   middle?: string[]
 *   dashboard?: string[]
 *   graph?: string[]
 *   referable?: 'all' | 'referable' | 'unreferable'
 * }} rowFilters
 * @param {{ [columnCode: string]: 'unset' | 'remove' | 'set' }} statusByCode
 * @returns {ReturnType<typeof buildLeftColumnFromSetting>}
 */
export function filterLeftColumnByRowFiltersFromStatus(rows, rowFilters, statusByCode) {
  if (!rowFilters || Object.keys(rowFilters).length === 0) return rows
  return rows.filter((r) => {
    if (Array.isArray(rowFilters.major)) {
      if (rowFilters.major.length === 0) return false
      if (!rowFilters.major.includes(r.majorLabel)) return false
    }
    if (Array.isArray(rowFilters.middle)) {
      if (rowFilters.middle.length === 0) return false
      if (!rowFilters.middle.includes(r.middleName)) return false
    }
    if (Array.isArray(rowFilters.dashboard)) {
      if (rowFilters.dashboard.length === 0) return false
      if (!rowFilters.dashboard.includes(r.dashboardName)) return false
    }
    if (Array.isArray(rowFilters.graph)) {
      if (rowFilters.graph.length === 0) return false
      if (!rowFilters.graph.includes(r.graphName)) return false
    }
    if (rowFilters.referable && rowFilters.referable !== 'all') {
      const referable = isRowReferableFromStatus(r, statusByCode)
      if (rowFilters.referable === 'referable' && !referable) return false
      if (rowFilters.referable === 'unreferable' && referable) return false
    }
    return true
  })
}

/**
 * StatusByCode を組み立てる際に必要な全 column_code 集合を作る。 横軸の全 column_code と、左カラム各行の required_columns / optional_columns を統合する。
 *
 * @param {ReturnType<typeof flattenHorizontalColumns>} flatColumns
 * @param {ReturnType<typeof buildLeftColumnFromSetting>} leftColumnRows
 * @returns {string[]}
 */
export function collectAllColumnCodes(flatColumns, leftColumnRows) {
  const codes = new Set()
  for (const c of flatColumns || []) {
    if (c.column_code) codes.add(c.column_code)
  }
  for (const r of leftColumnRows || []) {
    for (const code of r.required_columns || []) {
      if (code) codes.add(code)
    }
    // 任意項目もマッピング状況の取得対象。含めないと 任意 セルが常に 未設定 になる
    for (const code of r.optional_columns || []) {
      if (code) codes.add(code)
    }
  }
  return [...codes]
}
